import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  featuredItemsService, 
  FeaturedItemType, 
  FeaturedItem, 
  CreateFeaturedItemData, 
  UpdateFeaturedItemData 
} from '@/services/featuredItemsService';

// Hook para buscar todos os itens em destaque
export const useFeaturedItems = (itemType?: FeaturedItemType) => {
  return useQuery({
    queryKey: ['featured-items', itemType],
    queryFn: () => featuredItemsService.getFeaturedItems(itemType),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

// Hook para buscar itens em destaque para a home
export const useFeaturedItemsForHome = (itemType: FeaturedItemType) => {
  return useQuery({
    queryKey: ['featured-items-home', itemType],
    queryFn: () => featuredItemsService.getFeaturedItemsForHome(itemType),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para buscar itens em destaque para página específica
export const useFeaturedItemsForPage = (itemType: FeaturedItemType) => {
  return useQuery({
    queryKey: ['featured-items-page', itemType],
    queryFn: () => featuredItemsService.getFeaturedItemsForPage(itemType),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para verificar se um item está em destaque
export const useIsItemFeatured = (itemType: FeaturedItemType, itemId: string) => {
  return useQuery({
    queryKey: ['is-item-featured', itemType, itemId],
    queryFn: () => featuredItemsService.isItemFeatured(itemType, itemId),
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para buscar estatísticas dos destaques
export const useFeaturedStats = () => {
  return useQuery({
    queryKey: ['featured-stats'],
    queryFn: () => featuredItemsService.getFeaturedStats(),
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para criar item em destaque
export const useCreateFeaturedItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFeaturedItemData) => 
      featuredItemsService.createFeaturedItem(data),
    onSuccess: (newItem) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-home', newItem.item_type] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-page', newItem.item_type] });
      queryClient.invalidateQueries({ queryKey: ['featured-stats'] });
      queryClient.invalidateQueries({ queryKey: ['is-item-featured', newItem.item_type, newItem.item_id] });
      
      toast.success(`${newItem.item_type} adicionado aos destaques na posição ${newItem.position}!`);
    },
    onError: (error: Error) => {
      console.error('Erro ao criar item em destaque:', error);
      toast.error(error.message || 'Erro ao adicionar item aos destaques');
    },
  });
};

// Hook para atualizar item em destaque
export const useUpdateFeaturedItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeaturedItemData }) =>
      featuredItemsService.updateFeaturedItem(id, data),
    onSuccess: (updatedItem) => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-home', updatedItem.item_type] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-page', updatedItem.item_type] });
      queryClient.invalidateQueries({ queryKey: ['featured-stats'] });
      
      toast.success('Item em destaque atualizado com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar item em destaque:', error);
      toast.error(error.message || 'Erro ao atualizar item em destaque');
    },
  });
};

// Hook para remover item em destaque
export const useRemoveFeaturedItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => featuredItemsService.removeFeaturedItem(id),
    onSuccess: () => {
      // Invalidar todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-home'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-page'] });
      queryClient.invalidateQueries({ queryKey: ['featured-stats'] });
      queryClient.invalidateQueries({ queryKey: ['is-item-featured'] });
      
      toast.success('Item removido dos destaques com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro ao remover item em destaque:', error);
      toast.error(error.message || 'Erro ao remover item dos destaques');
    },
  });
};

// Hook para trocar posições
export const useSwapFeaturedPositions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id1, id2 }: { id1: string; id2: string }) =>
      featuredItemsService.swapPositions(id1, id2),
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['featured-items'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-home'] });
      queryClient.invalidateQueries({ queryKey: ['featured-items-page'] });
      
      toast.success('Posições trocadas com sucesso!');
    },
    onError: (error: Error) => {
      console.error('Erro ao trocar posições:', error);
      toast.error(error.message || 'Erro ao trocar posições');
    },
  });
};

// Hook personalizado para gerenciar destaques de um tipo específico
export const useFeaturedItemsManager = (itemType: FeaturedItemType) => {
  const featuredItems = useFeaturedItems(itemType);
  const createMutation = useCreateFeaturedItem();
  const updateMutation = useUpdateFeaturedItem();
  const removeMutation = useRemoveFeaturedItem();
  const swapMutation = useSwapFeaturedPositions();

  const addToFeatured = (itemId: string, position: number, options?: {
    featured_on_home?: boolean;
    featured_on_page?: boolean;
  }) => {
    createMutation.mutate({
      item_type: itemType,
      item_id: itemId,
      position,
      featured_on_home: options?.featured_on_home,
      featured_on_page: options?.featured_on_page,
    });
  };

  const updateFeatured = (id: string, data: UpdateFeaturedItemData) => {
    updateMutation.mutate({ id, data });
  };

  const removeFromFeatured = (id: string) => {
    removeMutation.mutate(id);
  };

  const swapPositions = (id1: string, id2: string) => {
    swapMutation.mutate({ id1, id2 });
  };

  return {
    // Data
    featuredItems: featuredItems.data || [],
    isLoading: featuredItems.isLoading,
    error: featuredItems.error,
    
    // Actions
    addToFeatured,
    updateFeatured,
    removeFromFeatured,
    swapPositions,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRemoving: removeMutation.isPending,
    isSwapping: swapMutation.isPending,
    
    // Refetch
    refetch: featuredItems.refetch,
  };
}; 